import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, ease as mmEase } from "@/lib/motion";
import { Bell, CheckCheck, ShieldAlert, Receipt as ReceiptIcon, Info, Sparkles } from "lucide-react";
import { api } from "@/lib/api";
import { relativeTime } from "@/lib/format";
import { toast } from "sonner";

const ez = mmEase.expo;

const ICON_MAP = {
  transaction: { icon: ReceiptIcon, bg: "bg-emerald/10",          color: "#10B981" },
  security:    { icon: ShieldAlert,  bg: "bg-gold/10",            color: "#F59E0B" },
  system:      { icon: Info,         bg: "bg-navy/[0.07]",         color: "#0B1D3A" },
};

export default function Notifications() {
  const [items,   setItems]   = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const load = () =>
    api.get("/notifications")
      .then(({ data }) => setItems(data))
      .finally(() => setLoading(false));

  useEffect(() => { load(); }, []);

  const markAll = async () => {
    try { await api.post("/notifications/read-all"); load(); }
    catch { toast.error("Could not mark notifications as read"); }
  };
  const markOne = async (id) => {
    try { await api.post(`/notifications/${id}/read`); load(); }
    catch {}
  };

  const handleClick = async (n) => {
    await markOne(n.id);
    const txId = n.tx_id || n.metadata?.tx_id;
    if (n.type === "transaction" && txId) navigate(`/app/transactions/${txId}`);
  };

  const unread = items.filter((n) => !n.read).length;

  return (
    <div className="space-y-5 max-w-2xl">

      {/* ── Header ──────────────────────────────────────────────── */}
      <motion.div
        initial={{ opacity: 0, y: -12, filter: "blur(4px)" }}
        animate={{ opacity: 1, y: 0,   filter: "blur(0px)" }}
        transition={{ duration: 0.45, ease: ez }}
        className="flex items-start justify-between gap-4"
      >
        <div>
          <p className="text-[10px] uppercase tracking-[0.22em] text-emerald font-bold mb-1">Updates</p>
          <h1 className="font-display text-[1.65rem] font-black text-navy tracking-tight">Notifications</h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            {unread > 0 ? (
              <><span className="font-semibold text-navy">{unread}</span> unread message{unread > 1 ? "s" : ""}</>
            ) : (
              "You're all caught up."
            )}
          </p>
        </div>
        {unread > 0 && (
          <button
            data-testid="notifications-mark-all"
            onClick={markAll}
            className="btn-ghost-dark shrink-0"
          >
            <CheckCheck size={13} /> Mark all read
          </button>
        )}
      </motion.div>

      {/* ── Skeleton ────────────────────────────────────────────── */}
      {loading && (
        <div className="card-elevated overflow-hidden divide-y divide-border/50">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="flex items-start gap-3.5 px-5 py-4">
              <div className="skeleton w-10 h-10 rounded-2xl shrink-0" />
              <div className="flex-1 space-y-2 pt-0.5">
                <div className="skeleton h-3 w-40 rounded" />
                <div className="skeleton h-2.5 w-56 rounded" />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── Empty state ─────────────────────────────────────────── */}
      {!loading && items.length === 0 && (
        <motion.div
          initial={{ opacity: 0, scale: 0.97 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.35, ease: ez }}
          className="card-elevated py-16 text-center"
        >
          <div className="w-14 h-14 rounded-2xl bg-navy/[0.06] grid place-items-center mx-auto text-navy/30 mb-4">
            <Sparkles size={22} strokeWidth={1.5} />
          </div>
          <p className="font-bold text-navy text-sm">No notifications yet</p>
          <p className="text-muted-foreground text-xs mt-1">Activity updates will appear here.</p>
        </motion.div>
      )}

      {/* ── List ────────────────────────────────────────────────── */}
      {!loading && items.length > 0 && (
        <motion.div
          className="card-elevated overflow-hidden"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, ease: ez, delay: 0.08 }}
        >
          <div className="divide-y divide-border/50">
            {items.map((n, i) => {
              const m    = ICON_MAP[n.type] || ICON_MAP.system;
              const Icon = m.icon;
              return (
                <motion.button
                  key={n.id}
                  data-testid={`notification-${n.id}`}
                  onClick={() => handleClick(n)}
                  className={`w-full text-left flex items-start gap-3.5 px-5 py-4 hover:bg-secondary/40 transition-colors group relative ${
                    !n.read ? "bg-emerald/[0.025]" : ""
                  }`}
                  initial={{ opacity: 0, x: -8 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.3, ease: ez, delay: 0.1 + i * 0.04 }}
                >
                  {/* Unread left-border accent */}
                  {!n.read && (
                    <span className="absolute left-0 inset-y-0 w-0.5 bg-emerald rounded-r-full" />
                  )}

                  <div
                    className={`w-10 h-10 rounded-2xl grid place-items-center shrink-0 mt-0.5 ${m.bg}`}
                    style={{ color: m.color }}
                  >
                    <Icon size={16} strokeWidth={1.8} />
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-navy text-[13px]">{n.title}</span>
                      {!n.read && (
                        <span className="w-1.5 h-1.5 rounded-full bg-emerald shrink-0" />
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground mt-0.5 leading-snug">{n.message}</p>
                    <p className="text-[10px] text-muted-foreground/70 mt-1.5">{relativeTime(n.created_at)}</p>
                  </div>
                </motion.button>
              );
            })}
          </div>
        </motion.div>
      )}
    </div>
  );
}
